/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { IsolateSelectionTransform } from "./isolate-selection.js";
import { computeSelection } from "../selection.js";
import { errorNoInspect } from "../operations/error.js";
export { MoveToGraphTransform };
class MoveToGraphTransform {
    #nodes;
    #source;
    #destination;
    constructor(nodes, source, destination) {
        this.#nodes = nodes;
        this.#source = source;
        this.#destination = destination;
    }
    #friendlyGraphName(id) {
        return id ? `graph "${id}"` : "main graph";
    }
    async apply(context) {
        const isolatedSelection = await new IsolateSelectionTransform(this.#nodes, this.#source).apply(context);
        if (!isolatedSelection.success) {
            return isolatedSelection;
        }
        const sourceInspector = context.mutable.graphs.get(this.#source);
        if (!sourceInspector) {
            return errorNoInspect(this.#source);
        }
        const selection = computeSelection(sourceInspector, this.#nodes);
        if (!selection.success) {
            return selection;
        }
        const nodeAdditions = selection.nodes.map((id) => ({
            type: "addnode",
            node: sourceInspector.nodeById(id).descriptor,
            graphId: this.#destination,
        }));
        const edgeAdditions = selection.edges.map((edge) => ({
            type: "addedge",
            edge,
            graphId: this.#destination,
        }));
        const nodeRemovals = selection.nodes.map((id) => ({
            type: "removenode",
            id,
            graphId: this.#source,
        }));
        const edgeRemovals = selection.edges.map((edge) => ({
            type: "removeedge",
            edge,
            graphId: this.#source,
        }));
        const label = `Moving ${this.#nodes.length} nodes from ${this.#friendlyGraphName(this.#source)} to ${this.#friendlyGraphName(this.#source)}`;
        const result = await context.apply([...nodeAdditions, ...edgeAdditions, ...edgeRemovals, ...nodeRemovals], label);
        if (!result.success) {
            return result;
        }
        return {
            success: true,
        };
    }
}
//# sourceMappingURL=move-to-graph.js.map